import { useCallback } from "react";
import { getAuth } from "firebase/auth";
import { useTheme } from "@/components/theme-provider";
import type { UiTheme } from "@/lib/ui-theme";
import { track, LogEvents } from "@/analytics/track";
import { app } from "@/config/firebaseConfig";
import { trpcClient } from "@/trpc/client";

type Theme = "light" | "dark" | "system";

/**
 * Persists the chosen visual theme to the user's profile (GRA-142) so it
 * follows them to another device, mirroring useChangeLanguage's approach.
 * Fire-and-forget: a failed write must not block the local switch, which has
 * already applied via `setUiTheme`.
 */
async function persistUiTheme(next: UiTheme): Promise<void> {
  const user = getAuth(app).currentUser;
  if (!user) return;

  try {
    await trpcClient.businessUser.updateUiTheme.mutate({ uiTheme: next });
  } catch (err) {
    console.error("Failed to persist UI theme preference", err);
  }
}

/**
 * Both theme axes behind one hook: light/dark/system and the visual theme
 * (Classic/Lilac). A UI theme change reuses the ThemeChanged event with a
 * `ui:` prefix, so no new event is needed in the catalogue.
 */
export function useChangeTheme() {
  const { theme, setTheme, uiTheme, setUiTheme } = useTheme();

  const changeTheme = useCallback(
    (next: Theme) => {
      setTheme(next);
      track({
        event: LogEvents.ThemeChanged.name,
        channel: LogEvents.ThemeChanged.channel,
        theme: next,
      });
    },
    [setTheme],
  );

  const changeUiTheme = useCallback(
    (next: UiTheme) => {
      setUiTheme(next);
      track({
        event: LogEvents.ThemeChanged.name,
        channel: LogEvents.ThemeChanged.channel,
        theme: `ui:${next}`,
      });
      void persistUiTheme(next);
    },
    [setUiTheme],
  );

  return { theme, changeTheme, uiTheme, changeUiTheme };
}
